import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, catchError, map, of, interval, Subscription } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { GlobalService, _baseUrl, _sentrackBaseUrl } from '../../helper/global-helper';
import { BasicMachineFunctionsService } from '../basicMachineFunctions/basic-machine-functions.service';

@Injectable({
    providedIn: 'root',
})
export class GatewayConnectionService {
    private baseUrl: string;
    private sentrackUrl: string;
    aliveCheckSub: Subscription;
    backendAlive: boolean = true;
    gatewayConnected: boolean = false;
    machineStatus: string = '';
    private connected: boolean = false;

    private machineid: string = '';

    constructor(private http: HttpClient, private global: GlobalService, private machineService: BasicMachineFunctionsService) {
        this.baseUrl = _baseUrl;  
        this.sentrackUrl = _sentrackBaseUrl;
        this.global.variables.subscribe({
            next: newValue => {this.connected = newValue.connected;}
        }); 
        this.global.machineNumber.subscribe({
            next: newValue => {this.machineid = newValue.id;}
        });
        this.aliveCheckSub = interval(3000).subscribe(() => {
            this.cyclicCheckBackendAlive();
            if(this.connected) {
                this.cyclicCheckGatewayConnection();
                //this.cyclicCheckMachineStatus();
            }
        });
    }

    private cyclicCheckBackendAlive() {
        this.checkBackendAlive().subscribe({
            next: (isAlive) => {
                this.backendAlive = isAlive;
            },
        });
    }

    private cyclicCheckGatewayConnection() {
        this.checkGatewayAlive().subscribe({
            next: (isAlive) => {
                this.gatewayConnected = isAlive;
            },
        });
    }

    private cyclicCheckMachineStatus() {
        this.fetchStatus();
    }

    public async establishConnectionToGateway(retries?: number, awaitResponseTimeMs?: number): Promise<void> {
        if (retries == undefined) {
            retries = 8;
        }
        if (awaitResponseTimeMs == undefined) {
            awaitResponseTimeMs = 1500;
        }

        let attempts = 0;

        while (attempts < retries) {
            if (this.gatewayConnected) {
                return new Promise<void>((resolve) => resolve());
            }
            console.log(`Connection Attempt ${attempts}`);
            attempts++;
            this.connectToGateway().subscribe((data): void => {});

            await new Promise<void>((resolve, reject) => {
                setTimeout(() => {
                    this.checkGatewayAlive().subscribe({
                        next: (isAlive) => {
                            console.log(isAlive);
                            this.gatewayConnected = isAlive;
                            if (isAlive) {
                                console.log('Connection successfull established');
                                resolve();
                            } else {
                                console.log('Connection failed');
                                reject();
                            }
                        },
                    });
                }, awaitResponseTimeMs);
            });
        }
        console.log('Maximum number of retries reached - Connection Abort');
    }

    checkBackendAlive(): Observable<boolean> {
        return this.http
            .get<{ backendAlive: boolean }>(this.baseUrl + 'server/alive', {
                observe: 'body',
                responseType: 'json',
            })
            .pipe(
                map((data) => data.backendAlive),
                catchError(() => of(false))
            );
    }

    checkGatewayAlive(): Observable<boolean> {
        return this.http
            .get<{ gatewayConnectionAlive: boolean }>(this.baseUrl + 'gatewayconnection/alive', {
                observe: 'body',
                responseType: 'json',
            })
            .pipe(
                map((data) => data.gatewayConnectionAlive),
                catchError(() => of(false))
            );
    }

    public connectToGateway() {
        console.log('Connect');
        let response = this.http.get(this.baseUrl + 'gatewayconnection/connect', {
            observe: 'body',
            responseType: 'json',
        });

        return response;
    }

    public disconnectFromGateway() {
        console.log('Disconnect');
        let response = this.http.delete(this.baseUrl + 'gatewayconnection/connect', {
            observe: 'body',
            responseType: 'json',
        });

        return response;
    }

    public getGatewayName() {
        return this.http.get(this.baseUrl + 'gatewayconnection/commonname', {
            observe: 'response',
            responseType: 'json',
        });
    }

    private checkMachineStatus(id: string, token: string){
        let headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        return this.http.get(this.sentrackUrl + "/client-sennebogen-business-service/machines/" + id + "?expand=status,type", {
            observe: 'response',
            responseType: 'json',
            headers: headers,
        });
    }

    public fetchStatus() {
        this.machineService.getExchangeToken().subscribe((res) => {
            let response = JSON.parse(JSON.stringify(res.body));
            this.checkMachineStatus(this.machineid, response.access_token).subscribe((resp) => {
                let state = JSON.parse(JSON.stringify(resp.body));
                if(state.status.status != undefined) {
                    this.machineStatus = state.status.status;
                } else {
                    this.machineStatus = 'OFFLINE';
                }
            });
        });
    }

    public connectToMachine(option: any, force?: boolean){
        let line = option.type.line;
        if(line == 'crane') {
            line = 'green';
        }
        let number = option.serialNumber;
        let imei = option.imei;
        let requestBody = {'option': option};
        if(force == undefined){
            return this.http.post(this.baseUrl + 'gatewayconnection/machine?machinenumber=' + number + '&gatewaycommonname=gateway-' + imei + '&productline=' + line + 'line', requestBody, {
                observe: 'response',
                responseType: 'json',
            });
        } else {
            return this.http.post(this.baseUrl + 'gatewayconnection/machine?machinenumber=' + number + '&gatewaycommonname=gateway-' + imei + '&productline=' + line + 'line' + '&force=' + force, requestBody, {
                observe: 'response',
                responseType: 'json',
            });
        }   
    }

    public registerUser(){
        return this.http.post(this.baseUrl + 'user/register', null, {
            observe: 'response',
            responseType: 'json',
        });
    }

    public getRegisteredUser() {
        return this.http.get(this.baseUrl + 'user/register', {
            observe: 'response',
            responseType: 'json',
        });
    }

    public deleteRegisteredUser() {
        return this.http.delete(this.baseUrl + 'user/register', {
            observe: 'response',
            responseType: 'json',
        });
    }

    public getBackendReleaseNotes(){
        return this.http.get(this.baseUrl + "version", {
            observe: 'response',
            responseType: 'json',
        });
    }

    setGatewayConnectionParameter(requestBody: JSON): Observable<HttpResponse<Object>> {
        let headers = new HttpHeaders().set('Content-Type', 'application/json');
        let response = this.http.put(this.baseUrl + 'gatewayconnection/parameters', requestBody, {
            observe: 'response',
            responseType: 'json',
            headers: headers,
        });
        return response;
    }

    resetGatewayConnectionParameter(): Observable<HttpResponse<Object>> {
        let response = this.http.delete(this.baseUrl + 'gatewayconnection/parameters', {
            observe: 'response',
            responseType: 'json',
        });
        return response;
    }

    fetchDiagnosticSessionInfo() {
        let response = this.http.get(this.baseUrl + 'admin/diagnosticsession', {
            observe: 'body',
            responseType: 'json',
        });
        return response;
    }
}
